import { Box, Button, Paper, TextField, Typography, useMediaQuery, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import React, { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import Toast from "../utils/Toast";
import Loader from "../utils/Loader";

import { UserInfo } from "../../helpers";
import { SnackbarInterface } from "../utils";

import "./loginPanel.scss";

interface LoginPanelProps {
  loading: boolean;
  error: string | null;
  errorHandler: () => void;
  handleSubmitHandler: (data: {
    app_id: string;
    app_url: string;
    user_id: string;
    password: string;
  }) => void;
  userLoginInfo: UserInfo,
  showNote: boolean
}

const LoginPanel = ({
  loading,
  error,
  errorHandler,
  handleSubmitHandler,
  showNote,
  userLoginInfo
}: LoginPanelProps) => {

  const navigate = useNavigate();
  const isTabletView = useMediaQuery('(max-width: 960px)');

  const [user, setUser] = useState({
    user_id: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  const [snackbar, setSnackbar] = useState<SnackbarInterface>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({
      open: false,
      message: "",
      severity: "info",
    });
    if (error?.length) {
      errorHandler();
    }
  };

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUser((prev: any) => {
      return {
        ...prev,
        [event.target.name]: event.target.value,
      };
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (user.user_id && user.password) {
      const data = {
        ...user,
        app_id: "",
        app_url: "",
      };
      handleSubmitHandler(data);
    } else {
      setSnackbar({
        open: true,
        message: "Value missing",
        severity: "error",
      });
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    error?.length &&
      setSnackbar({
        open: true,
        message: error,
        severity: "error",
      });
  }, [error]);

  useEffect(() => {
    userLoginInfo
      ? userLoginInfo.user_key.length
        ? userLoginInfo.role !== "User"
          ? navigate("/admin/accounts")
          : navigate("/accounts")
        : navigate("/login")
      : navigate("/login");
  }, [navigate, userLoginInfo]);

  return (
    <Box className="login-container">
      {loading && <Loader open={loading} />}
      {snackbar.open && <Toast snackbar={snackbar} handleCloseSnackbar={handleCloseSnackbar} />}

      <Paper elevation={1} className="login-component-wrapper ">
        <Box className="login-wrapper">
          <Typography variant='h5' >Login to your Account</Typography>
          {showNote && <Box className="card-note">
            <Typography className="text">Please fill out the following form with your login credentials.</Typography>
          </Box>}
          <Box className="login-comp-body">
            <form onSubmit={handleSubmit} className="login-wrapper-fields">
              <TextField
                name="user_id"
                label="Username"
                placeholder="username@myaccountplus.com"
                fullWidth
                required
                focused
                value={user.user_id}
                onChange={onChangeHandler}
                InputLabelProps={{ shrink: true }}
                className={user.user_id.length > 0 ? "MuiCustom-success" : ""}
              />
              <TextField
                name="password"
                label="Password"
                placeholder="*********"
                type={showPassword ? "text" : "password"}
                fullWidth
                required
                focused
                value={user.password}
                onChange={onChangeHandler}
                InputLabelProps={{ shrink: true }}
                autoComplete="on"
                className={user.password.length > 0 ? "MuiCustom-success" : ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography color="secondary">
                <Link to="/forget-password">
                  <Button color="secondary">Forgot Password?</Button>
                </Link>
              </Typography>
              <Button aria-label="login" variant="contained" type="submit" fullWidth disabled={!user.user_id || !user.password}>
                Log In
              </Button>
            </form>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginPanel;
